import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import styled from 'styled-components';

const TimelineContainer = styled(Container)`
  max-width: 1080px;
  margin: 0 auto;
  padding: 28px 20px;
  background: rgba(10, 10, 10, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);
`;

const TimelineWrapper = styled.div`
  position: relative;
  padding: 32px 0;
  margin: 20px 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.75), rgba(255, 255, 255, 0.12));
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    &::before {
      left: 20px;
    }
  }
`;

const TimelineContent = styled.div`
  position: relative;
  padding: 24px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
  overflow: hidden;

  h4 {
    margin: 0 0 10px;
    color: #ffffff;
    font-size: 1.2rem;
    letter-spacing: 0.02em;
  }

  .year {
    margin: 0 0 14px;
    font-size: 0.95rem;
    color: #b9b9b9;
    letter-spacing: 0.04em;
  }

  p {
    margin: 0;
    color: #e6e6e6;
    line-height: 1.75;
    font-size: 0.96rem;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top left, rgba(160, 84, 255, 0.18), transparent 38%);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.35s ease;
  }

  &:hover {
    transform: translateY(-5px);
    background: rgba(139, 92, 246, 0.16);
    border-color: rgba(160, 84, 255, 0.35);
  }

  &:hover::after {
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 18px;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin: 28px 0;
  padding-left: 50%;
  clear: both;
  opacity: 0;
  transform: translateY(30px) scale(0.98);
  transition: opacity 0.55s ease, transform 0.55s ease;

  &:nth-child(odd) {
    padding-left: 0;
    padding-right: 50%;
  }

  &::before {
    content: '';
    position: absolute;
    top: 24px;
    left: 50%;
    width: 20px;
    height: 20px;
    margin-left: -10px;
    background: radial-gradient(circle at top left, #b57cff, #7d3cff);
    border: 3px solid #121212;
    box-shadow: 0 0 0 6px rgba(144, 75, 255, 0.12);
    border-radius: 50%;
    z-index: 1;
    transition: transform 0.3s ease;
  }

  &.visible {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  &:hover::before {
    transform: translateY(-2px) scale(1.08);
  }

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
    margin-left: 0;
    margin-right: 0;

    &::before {
      left: 20px;
      margin-left: 0;
    }

    ${TimelineContent} {
      margin-left: 48px;
    }
  }
`;

export const Timeline = () => {
    const [visibleItems, setVisibleItems] = useState([]);
    const itemRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = Number(entry.target.dataset.index);
                        setVisibleItems((current) => (current.includes(index) ? current : [...current, index]));
                    }
                });
            },
            { threshold: 0.2 }
        );

        itemRefs.current.forEach((item) => item && observer.observe(item));

        return () => observer.disconnect();
    }, []);

    const events = [
        { title: 'Huawei Malaysia', year: 'Jul 2026 - Dec 2026', description: 'Coming Soon.' },
        {
            title: 'Core Consulting and Innovation',
            year: 'Jul 2025 - Oct 2025',
            description:
                'As an AIOps Intern, I contributed to AI-powered analytics projects that transformed complex operational data into actionable insights using Dynatrace. I collaborated with cross-functional teams to identify business pain points, develop live dashboard metrics, and enhance data-driven decision-making across industries. This experience strengthened my analytical thinking, technical problem-solving, and understanding of real-world AIOps applications.',
        },
        {
            title: 'Bachelor’s Degree in Computer Science (Hons.)',
            year: 'Aug 2023 - July 2026',
            description:
                'Currently pursuing my bachelor\'s degree. I served as the President of the Information Technology Society for the 2023/2024 term, leading a team of 33 members in organizing various tech events for the faculty students. In addition, I have participated in several hackathons and tech community events.',
        },
        {
            title: 'Foundation in Information Technology',
            year: 'Aug 2022 - July 2023',
            description:
                'Successfully completed my foundation program with a first-class CGPA and received the Dean\'s List Award for multiple trimesters. I was the Head of Public Relations and Marketing for the Information Technology Society at MMU Cyberjaya, where I collaborated with various companies for potential partnerships. Additionally, I served as the Vice Director of CodeNection 2023 and Head of Sponsorship for multiple events, including Tech Career Days 2023, BarCamp 2023, and the MMU Open Chess Championship 2023.',
        },
        {
            title: 'Secondary Education',
            year: 'Jan 2017 - Mar 2022',
            description:
                'Achieved straight A\'s in both SPM and PT3. I held several leadership roles, including Head Prefect of my school\'s Prefect Organization, President of the Science, Technology, Engineering & Mathematics (STEM) Club, and President of the Malaysian Scouts Association. I was also a district-level public speaker and school player for the basketball team.',
        },
    ];

    return (
        <TimelineContainer>
            <Row>
                <Col>
                    <TimelineWrapper>
                        {events.map((event, index) => (
                            <TimelineItem
                                key={index}
                                ref={(el) => {
                                    itemRefs.current[index] = el;
                                }}
                                data-index={index}
                                className={visibleItems.includes(index) ? 'visible' : ''}
                            >
                                <TimelineContent>
                                    <h4>{event.title}</h4>
                                    <div className="year">{event.year}</div>
                                    <p>{event.description}</p>
                                </TimelineContent>
                            </TimelineItem>
                        ))}
                    </TimelineWrapper>
                </Col>
            </Row>
        </TimelineContainer>
    );
};